import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Router, Route, Switch, Redirect } from "wouter";
import { useLocation } from "wouter";
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { SimpleROIProvider } from "./contexts/SimpleROIContext";
import { StandardROIProvider } from "./contexts/StandardROIContext";
import { FloatingFeedbackButton } from "./components/FloatingFeedbackButton";
import { FloatingHelpButton } from "./components/FloatingHelpButton";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import NotFound from "@/pages/not-found";
import LoginScreen from "./pages/LoginScreen";
import HelpCenter from "./pages/HelpCenter";

// Import all pages
import TransformationGoals from "./pages/TransformationGoals";
import PDFExportLogViewer from "./components/PDFExportLogViewer";
import AdminProjectsPage from "./pages/admin/AdminProjectsPage";
import AdminProjectDetailPage from "./pages/admin/AdminProjectDetailPage";
import AdminPricingMatrixScreen from "./pages/admin/AdminPricingMatrixScreen";
import AdminPricingTiersScreen from "./pages/admin/AdminPricingTiersScreen";
import AdminHelpScreen from "./pages/admin/AdminHelpScreen";
import AdminUserManagementScreen from "./pages/admin/AdminUserManagementScreen";
import AdminQuotesScreen from "./pages/admin/AdminQuotesScreen";
import BlobPreviewScreen from "./pages/BlobPreviewScreen";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import AdminRouteGuard from "./routes/AdminRouteGuard";
import AuthRouteGuard from "./routes/AuthRouteGuard";
import AdminRecommendationsEditor from "./pages/admin/AdminRecommendationsEditor";
import ExportAuditScreen from "./pages/admin/ExportAuditScreen";
import SystemSettingsScreen from "./pages/admin/SystemSettingsScreen";
import AdminBenchmarksScreen from "./pages/admin/AdminBenchmarksScreen";
import AdminVaPricingScreen from "./pages/admin/AdminVaPricingScreen";
import ContentHubScreen from "./pages/admin/ContentHubScreen";
import BenchmarksHubScreen from "./pages/admin/BenchmarksHubScreen";
import AdminAccessControl from "./pages/admin/AdminAccessControl";

// Import new admin screens
import AssessmentDataScreen from "./pages/admin/AssessmentDataScreen";
import InvitationsScreen from "./pages/admin/InvitationsScreen";
import AssessmentsReport from "./pages/admin/AssessmentsReport";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRationaleDefaultsScreen from "./pages/admin/AdminRationaleDefaultsScreen";

// Placeholder imports for remaining screens
const ExportMonitoringScreen = () => <div>Export Monitoring Screen - Coming Soon</div>;
const UserFeedbackScreen = () => <div>User Feedback Screen - Coming Soon</div>;
const ActivityLogsScreen = () => <div>Activity Logs Screen - Coming Soon</div>;

import AdminLayout from "./components/admin/AdminLayout";

import ProjectHistoryScreen from "./pages/ProjectHistoryScreen";
import ProjectUserManagement from "./pages/ProjectUserManagement";
import { PublicShareScreen } from "./pages/PublicShareScreen";
import ProjectSelectionScreen from "./pages/ProjectSelectionScreen";
import ModuleSelectionScreen from "./pages/ModuleSelectionScreen";
import StyleGuide from "./pages/StyleGuide";
import { TaskTableDemo } from "./pages/TaskTableDemo";

import ValueAssessmentLayout from "./pages/value-assessment/ValueAssessmentLayout";
import IntroScreen from "./pages/value-assessment/IntroScreen";
import PublicIntroScreen from "./pages/value-assessment/PublicIntroScreen";
import ProjectKickoffScreen from "./pages/value-assessment/ProjectKickoffScreen";
import StrategicContextScreen from "./pages/value-assessment/StrategicContextScreen";
import TaskFirstFTEAllocationScreen from "./pages/value-assessment/TaskFirstFTEAllocationScreen";
import PublicInputFlow from "./pages/value-assessment/PublicInputFlow";
import ValueAssessmentPlatformConfigurationScreen from "./pages/value-assessment/PlatformConfigurationScreen";
import ValueAssessmentSummary from "./pages/value-assessment/ValueAssessmentSummary";
import PublicCostSavingsSummary from "./pages/PublicCostSavingsSummary";
import CostSavingsSummaryPage from "./pages/CostSavingsSummaryPage";

import InAppAssessment from "./pages/maturity-assesment/InAppAssessment";
import MaturityOnlyResults from "@/pages/maturity-assesment/MaturityOnlyResults.tsx";
import MaturityPrintPage from "@/pages/maturity-assesment/MaturityPrintPage";

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <AuthProvider>
          <ProjectProvider>
            <SimpleROIProvider>
                <StandardROIProvider>
                  <ErrorBoundary>
                  <div className="font-sans">
                    <ImpersonationBanner />
                    <Router>
                      {/* Global auth guard protects all routes except the allowlist */}
                      <AuthRouteGuard allowedPaths={["/share/*","/public/cost-savings-summary/*","/value-assessment/public-input/*","/value-assessment/input*","/value-assessment/*/public-intro","/value-assessment/*/kickoff","/value-assessment/*/context","/value-assessment/*/allocation","/projects/*/assessment"]} />
                      <Switch>
                  {/* Public sharing routes - no authentication required */}
                  <Route path="/share/:module/:token" component={PublicShareScreen} />
                  
                  {/* Public Cost Savings Summary - no authentication required */}
                  <Route path="/public/cost-savings-summary/:token" component={PublicCostSavingsSummary} />
                  
                  {/* Private Cost Savings Summary - authentication required */}
                  <Route path="/cost-savings-summary/:projectId" component={CostSavingsSummaryPage} />
                  
                  {/* Public Input Flow - no authentication required */}
                  <Route path="/value-assessment/public-input/:projectId" component={PublicInputFlow} />
                  
                  {/* Deep linking routes for input screens */}
                  <Route path="/value-assessment/input" component={PublicInputFlow} />
                  <Route path="/value-assessment/input/:screen" component={PublicInputFlow} />

                  
                  <Route path="/" component={ProjectSelectionScreen} />
                  <Route path="/login" component={LoginScreen} />
                  <Route path="/project/:id" component={ModuleSelectionScreen} />
                  


                  {/* Value Assessment Routes - Standalone Assessment Flow */}
                  <Route path="/value-assessment/:projectId/intro" component={() => <ValueAssessmentLayout><IntroScreen /></ValueAssessmentLayout>} />
                  <Route path="/value-assessment/:projectId/public-intro" component={PublicIntroScreen} />
                  <Route path="/value-assessment/:projectId/kickoff" component={() => <ValueAssessmentLayout><ProjectKickoffScreen /></ValueAssessmentLayout>} />
                  <Route path="/value-assessment/:projectId/context" component={() => <ValueAssessmentLayout><StrategicContextScreen /></ValueAssessmentLayout>} />
                  <Route path="/value-assessment/:projectId/allocation" component={() => <ValueAssessmentLayout><TaskFirstFTEAllocationScreen /></ValueAssessmentLayout>} />
                  <Route path="/value-assessment/:projectId/configuration" component={() => <ValueAssessmentLayout><ValueAssessmentPlatformConfigurationScreen /></ValueAssessmentLayout>} />


                  <Route path="/value-assessment/:projectId/value-assessment-summary" component={() => <ValueAssessmentLayout><ValueAssessmentSummary /></ValueAssessmentLayout>} />

                  
                  {/* Admin Routes - Protected with AdminRouteGuard */}
                  <Route path="/admin" component={() => <AdminRouteGuard><AdminLayout><AdminDashboard /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/dashboard" component={() => <AdminRouteGuard><AdminLayout><AdminDashboard /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/users" component={() => <AdminRouteGuard><AdminLayout><AdminUserManagementScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/projects" component={() => <AdminRouteGuard><AdminLayout><AdminProjectsPage /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/access" component={() => <AdminRouteGuard><AdminLayout><AdminAccessControl /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/projects/:id" component={() => <AdminRouteGuard><AdminLayout><AdminProjectDetailPage /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/recommendations" component={() => <AdminRouteGuard><AdminLayout><AdminRecommendationsEditor /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/quotes" component={() => <AdminRouteGuard><AdminLayout><AdminQuotesScreen /></AdminLayout></AdminRouteGuard>} />

                  <Route path="/admin/export-audit" component={() => <AdminRouteGuard><AdminLayout><ExportAuditScreen /></AdminLayout></AdminRouteGuard>} />
                  {/* Content Management Routes */}
                  <Route path="/admin/content-hub" component={() => <AdminRouteGuard><AdminLayout><ContentHubScreen /></AdminLayout></AdminRouteGuard>} />

                  <Route path="/admin/help-content" component={() => <AdminRouteGuard><AdminLayout><AdminHelpScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/rationale-defaults" component={() => <AdminRouteGuard><AdminLayout><AdminRationaleDefaultsScreen /></AdminLayout></AdminRouteGuard>} />
                  
                  {/* Data & Benchmarks Routes */}
                  <Route path="/admin/benchmarks-hub" component={() => <AdminRouteGuard><AdminLayout><BenchmarksHubScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/benchmarks" component={() => <AdminRouteGuard><AdminLayout><AdminBenchmarksScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/assessment-data" component={() => <AdminRouteGuard><AdminLayout><AssessmentDataScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/assessments-report" component={() => <AdminRouteGuard><AdminLayout><AssessmentsReport /></AdminLayout></AdminRouteGuard>} />
                  
                  {/* System Configuration Routes */}
                  <Route path="/admin/pricing-matrix" component={() => <AdminRouteGuard><AdminLayout><AdminPricingMatrixScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/pricing-tiers" component={() => <AdminRouteGuard><AdminLayout><AdminPricingTiersScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/exports" component={() => <AdminRouteGuard><AdminLayout><ExportAuditScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/help" component={() => <AdminRouteGuard><AdminLayout><AdminHelpScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/system-settings" component={() => <AdminRouteGuard><AdminLayout><SystemSettingsScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/pricing-config" component={() => <AdminRouteGuard><AdminLayout><AdminVaPricingScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/system-config" component={() => <AdminRouteGuard><AdminLayout><SystemSettingsScreen /></AdminLayout></AdminRouteGuard>} />
                  
                  {/* User Management Routes */}
                  <Route path="/admin/invitations" component={() => <AdminRouteGuard><AdminLayout><InvitationsScreen /></AdminLayout></AdminRouteGuard>} />
                  
                  {/* Audit & Monitoring Routes */}
                  <Route path="/admin/export-monitoring" component={() => <AdminRouteGuard><AdminLayout><ExportMonitoringScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/user-feedback" component={() => <AdminRouteGuard><AdminLayout><UserFeedbackScreen /></AdminLayout></AdminRouteGuard>} />
                  <Route path="/admin/activity-logs" component={() => <AdminRouteGuard><AdminLayout><ActivityLogsScreen /></AdminLayout></AdminRouteGuard>} />
                  
                  {/* Legacy Routes - Redirect to new structure */}
                  <Route path="/admin/va-pricing">
                    {() => <Redirect to="/admin/pricing-config" />}
                  </Route>
                  <Route path="/admin/help">
                    {() => <Redirect to="/admin/help-content" />}
                  </Route>
                  <Route path="/admin/module-visibility">
                    {() => <Redirect to="/admin/module-config" />}
                  </Route>
                  <Route path="/admin/system-settings">
                    {() => <Redirect to="/admin/system-config" />}
                  </Route>
                  <Route path="/admin/nudges">
                    {() => <Redirect to="/admin/maturity-content" />}
                  </Route>

                  {/* Super Admin Development Routes */}
                  <Route path="/value-assessment/:projectId/blob-preview" component={() => <AdminRouteGuard><BlobPreviewScreen /></AdminRouteGuard>} />
                  
                  {/* Project Management Routes */}
                  <Route path="/projects/:id/history" component={ProjectHistoryScreen} />
                  <Route path="/projects/:id/users" component={ProjectUserManagement} />
                  
                  {/* Invitation Routes */}
                  <Route path="/unauthorized" component={UnauthorizedPage} />
                  
                  {/* Export and Log Routes */}
                  <Route path="/export-log" component={PDFExportLogViewer} />
                  <Route path="/transformation-goals" component={TransformationGoals} />
                  
                  {/* In-App Assessment Route */}
                  <Route path="/projects/:projectId/assessment" component={InAppAssessment} />
                  <Route path="/maturity/:id/results" component={MaturityOnlyResults} />
                  <Route path="/projects/:id/maturity-results" component={MaturityOnlyResults} />
                  <Route path="/print/maturity/:projectId" component={MaturityPrintPage} />

                  {/* Help Center */}
                  <Route path="/help-center" component={HelpCenter} />
                  
                  {/* Style Guide */}
                  <Route path="/styleguide" component={StyleGuide} />
                  <Route path="/task-table-demo" component={TaskTableDemo} />
                  

                  
                  {/* Deprecated route redirects */}
                  <Route path="/projects" component={() => { 
                    const [, setLocation] = useLocation();
                    React.useEffect(() => setLocation('/'), [setLocation]);
                    return null;
                  }} />
                  
                  {/* Not Found */}
                  <Route component={NotFound} />
                      </Switch>
                    </Router>
                    <FloatingHelpButton />
                    <FloatingFeedbackButton />
                    <Toaster />
                  </div>
                </ErrorBoundary>
              </StandardROIProvider>
          </SimpleROIProvider>
        </ProjectProvider>
      </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
